/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {reInitializeState} from '@ion/applications/src/helpers';
import {Route} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {PageState} from '@/types/pages';
import {
  AdminAction,
  AsyncState
} from '@/types/redux';
import {
  ADD_ADMIN_ROUTE,
  ADD_ADMIN_ROUTE_ERROR,
  ADD_ADMIN_ROUTE_REQUEST,
  ADD_ADMIN_ROUTE_RESPONSE,
  ADMIN_ROUTES_GRID_REFRESHED,
  DELETE_ADMIN_ROUTE,
  DELETE_ADMIN_ROUTE_ERROR,
  DELETE_ADMIN_ROUTE_REQUEST,
  DELETE_ADMIN_ROUTE_RESPONSE,
  EDIT_ADMIN_ROUTE,
  EDIT_ADMIN_ROUTE_ERROR,
  EDIT_ADMIN_ROUTE_REQUEST,
  EDIT_ADMIN_ROUTE_RESPONSE,
  GET_ADMIN_ROUTES_ERROR,
  GET_ADMIN_ROUTES_REQUEST,
  GET_ADMIN_ROUTES_RESPONSE,
  INITIALIZE_ADMIN_ROUTES,
  RE_INITIALIZE_ADMIN_ROUTES,
  SET_ADMIN_ROUTES_GRID_FILTER_MODEL,
  SET_GRID_COLUMNS,
  UPDATE_GRID_COLUMNS
} from '../actions/admin-routes';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '../helpers';

export interface AdminRoutesActionState extends AsyncState {
  selected: Route | Route[] | null;
  result: Route | Route[] | null;
  isEditing: boolean;
};

export interface AdminRoutesAction extends AdminAction {
  route?: Route;
  routes?: Route[];
};

export interface AdminRoutesState extends PageState {
  routes: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    filterModel: FilterModel | {}
  },
  add: AdminRoutesActionState,
  edit: AdminRoutesActionState,
  destroy: AdminRoutesActionState
}

// defines the preference key for the admin routes page ...
const PREFERENCE = 'admin-routes';

// defines the initial state for the reducer ...
export const initialState: AdminRoutesState = {
  isInitialized : false,
  routes        : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    filterModel         : {}
  },
  add : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  edit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  destroy : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  }
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_ADMIN_ROUTES] : (draft: AdminRoutesState) => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
    draft.destroy.error            = null;
  },
  [RE_INITIALIZE_ADMIN_ROUTES] : (draft: AdminRoutesState) => {
    reInitializeState(draft, {initialState});
  },
  [SET_GRID_COLUMNS] : ({grid, routes}: AdminRoutesState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : routes.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [SET_ADMIN_ROUTES_GRID_FILTER_MODEL] : ({grid}: AdminRoutesState, {model}) => {
    grid.filterModel = model;
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: AdminRoutesState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [ADMIN_ROUTES_GRID_REFRESHED] : (
    {add, edit, destroy}: AdminRoutesState
  ) => {
    add.result     = null;
    edit.result    = null;
    destroy.result = null;
  },
  [GET_ADMIN_ROUTES_REQUEST] : ({routes}: AdminRoutesState) => {
    routes.loading = true;
  },
  [GET_ADMIN_ROUTES_RESPONSE] : ({routes}: AdminRoutesState) => {
    routes.loading = false;
  },
  [GET_ADMIN_ROUTES_ERROR] : ({routes}: AdminRoutesState, {error}: AdminRoutesAction) => {
    routes.error = error;
    routes.loading = false;
  },
  [ADD_ADMIN_ROUTE] : ({add}: AdminRoutesState, {show}: AdminRoutesAction) => {
    add.isEditing = show;
    add.result    = null;
  },
  [ADD_ADMIN_ROUTE_REQUEST] : ({add}: AdminRoutesState) => {
    add.loading = true;
    add.error   = add.result = null;
  },
  [ADD_ADMIN_ROUTE_RESPONSE] : ({add}: AdminRoutesState, {route}: AdminRoutesAction) => {
    add.loading   = false;
    add.isEditing = false;
    add.result    = route;
  },
  [ADD_ADMIN_ROUTE_ERROR] : ({add}: AdminRoutesState, {error}: AdminRoutesAction) => {
    add.loading   = false;
    add.error     = error;
  },
  [EDIT_ADMIN_ROUTE] : ({edit}: AdminRoutesState, {route, show}: AdminRoutesAction) => {
    edit.selected  = show ? route : null;
    edit.isEditing = show;
    edit.result    = null;
  },
  [EDIT_ADMIN_ROUTE_REQUEST] : ({edit}: AdminRoutesState) => {
    edit.loading = true;
    edit.error   = edit.result = null;
  },
  [EDIT_ADMIN_ROUTE_RESPONSE] : ({edit}: AdminRoutesState, {route}: AdminRoutesAction) => {
    edit.loading   = false;
    edit.isEditing = false;
    edit.result    = route;
  },
  [EDIT_ADMIN_ROUTE_ERROR] : ({edit}: AdminRoutesState, {error}: AdminRoutesAction) => {
    edit.loading   = false;
    edit.error     = error;
  },
  [DELETE_ADMIN_ROUTE] : ({destroy}: AdminRoutesState, {route, show}: AdminRoutesAction) => {
    destroy.selected  = show ? route : null;
    destroy.isEditing = show;
    destroy.result    = null;
    destroy.loading   = false;
  },
  [DELETE_ADMIN_ROUTE_REQUEST] : ({destroy}: AdminRoutesState) => {
    destroy.loading = true;
    destroy.error   = destroy.result = null;
  },
  [DELETE_ADMIN_ROUTE_RESPONSE] : ({destroy}: AdminRoutesState, {route}: AdminRoutesAction) => {
    destroy.loading   = false;
    destroy.isEditing = false;
    destroy.result    = route;
  },
  [DELETE_ADMIN_ROUTE_ERROR] : ({destroy}: AdminRoutesState, {error}: AdminRoutesAction) => {
    destroy.loading = false;
    destroy.error   = error;
    destroy.result  = null;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: AdminRoutesState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
